import { useEffect, useRef, useState } from 'react'
import type { MappingAiStatus, MappingFieldChecks } from '../../types'
import { Head, Link, router, useForm } from '@inertiajs/react'
import MappingForm from '../../components/MappingForm'
import { eyebrowClass, linkClass } from '../../components/ui'
import type { ColumnMapping, MappingField } from '../../types/launch'

type Props = {
  launch_session: { id: number; name: string }
  headers: string[]
  units_row: string[]
  sample_rows: (string | null)[][]
  fields: MappingField[]
  suggestions: ColumnMapping
  field_checks: MappingFieldChecks
  ai_status: MappingAiStatus
  ai_notice: string
  remembered_from: 'you' | 'another_golfer' | null
}

export default function Mapping({ launch_session, headers, units_row, sample_rows, fields, suggestions, remembered_from, field_checks, ai_status, ai_notice }: Props) {
  const form = useForm<{ mapping: ColumnMapping }>({ mapping: suggestions })

  const pollWindow = useRef<{ session: number; started: number } | null>(null)
  const stopPolling = useRef<() => void>(() => {})
  const [pollExpired, setPollExpired] = useState(false)

  useEffect(() => {
    if (ai_status !== 'pending' || form.processing) return
    if (pollWindow.current?.session !== launch_session.id) {
      pollWindow.current = { session: launch_session.id, started: Date.now() }
      setPollExpired(false)
    }
    const deadline = pollWindow.current.started + 20000
    if (Date.now() >= deadline) {
      setPollExpired(true)
      return
    }
    let active = true
    const requests = new Set<{ cancel: () => void }>()
    const timer = window.setInterval(() => {
      if (!active) return
      if (Date.now() > deadline) {
        window.clearInterval(timer)
        setPollExpired(true)
        return
      }
      router.reload({
        only: ['suggestions', 'field_checks', 'ai_status'],
        onCancelToken: token => { if (active) requests.add(token); else token.cancel() },
      })
      if (Date.now() >= deadline) {
        window.clearInterval(timer)
        setPollExpired(true)
      }
    }, 2000)
    const stop = () => {
      active = false
      window.clearInterval(timer)
      requests.forEach(request => request.cancel())
      requests.clear()
    }
    stopPolling.current = stop
    return stop
  }, [ai_status, form.processing, launch_session.id])

  return (
    <>
      <Head title={`Match columns · ${launch_session.name}`} />
      <Link href="/launch_sessions" className={`${linkClass} text-sm`}>← All sessions</Link>
      <header className="mt-6 mb-8">
        <p className={`${eyebrowClass} text-muted`}>One file, a little context</p>
        <h1 className="mt-3 font-serif text-4xl tracking-tight sm:text-5xl">Match your columns</h1>
        <p className="mt-4 break-words text-base leading-7 text-muted">{launch_session.name} · Tell us which measurements your CSV contains.</p>
      </header>
      <p className="mb-4 text-sm leading-6 text-muted">{ai_notice}</p>
      {ai_status === 'pending' && !pollExpired && <p role="status" className="mb-4 text-sm text-muted">Looking for column suggestions… You can start matching columns now.</p>}
      {ai_status === 'pending' && pollExpired && <p className="mb-4 text-sm text-muted">Suggestions are taking longer than expected. You can still match columns manually.</p>}
      {ai_status === 'failed' && <p className="mb-4 text-sm text-muted">AI suggestions are unavailable right now. You can still match columns manually.</p>}
      {ai_status === 'capped' && <p className="mb-4 text-sm text-muted">The AI suggestion limit has been reached. You can still match columns manually.</p>}
      {remembered_from && <p role="status" className="mb-7 rounded-lg border border-notice-border bg-notice p-4 text-sm leading-7 text-notice-ink">
        {remembered_from === 'you'
          ? "You've used this layout before — check the pre-filled columns"
          : 'Another golfer mapped this layout — check every column and unit before importing'}
      </p>}
      <section aria-labelledby="preview-title" className="mb-9">
        <h2 id="preview-title" className="mb-3 font-serif text-2xl">A look inside your file</h2>
        <div role="region" aria-label="CSV preview" tabIndex={0} className="overflow-x-auto rounded-lg border border-line bg-surface focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand">
          <table className="w-full text-left text-sm">
            <caption className="sr-only">First rows from your CSV, with units from the file where provided</caption>
            <thead className="border-b border-line bg-soft"><tr>{headers.map((header, index) => <th key={header} scope="col" className="min-w-36 px-4 py-3 font-medium">{header}{units_row[index] && <span className="mt-1 block font-mono text-xs font-normal text-muted">{units_row[index]}</span>}</th>)}</tr></thead>
            <tbody className="divide-y divide-line">
              {sample_rows.map((row, rowIndex) => <tr key={rowIndex}>{headers.map((header, columnIndex) => <td key={header} className="max-w-xs break-words px-4 py-3 text-muted">{row[columnIndex] === null || row[columnIndex] === undefined || row[columnIndex] === '' ? <span aria-label="Empty cell">—</span> : row[columnIndex]}</td>)}</tr>)}
            </tbody>
          </table>
        </div>
        {sample_rows.length === 0 && <p className="mt-3 text-sm text-muted">No preview rows were provided.</p>}
        <p className="mt-2 text-xs text-muted">Scroll across to see all columns. Empty cells stay empty.</p>
      </section>
      <MappingForm key={launch_session.id} headers={headers} fields={fields} suggestions={suggestions} field_checks={field_checks} processing={form.processing} error={form.errors.mapping}
        onSubmit={mapping => {
          stopPolling.current()
          form.transform(() => ({ mapping }))
          form.patch(`/launch_sessions/${launch_session.id}/mapping`)
        }} />
    </>
  )
}
